import { css, keyframes } from 'styled-components'

type Props = {
  height?:number
  reverse?: boolean
  color?: string
}

const underlineReappear = (height) => keyframes`
  from {
    background-size: 0% ${height}px;
    background-position: 0 96%;
  }
  to {
    background-size: 100% ${height}px;
    background-position: 0 96%;
  }
`

const underlineDisappear = (height) => keyframes`
  from {
    background-size: 100% ${height}px;
    background-position: 0 96%;
  }
  to {
    background-size: 0 ${height}px;
    background-position: 100% 96%;
  }
`

export const createUnderline = ({ height = 1, reverse = false,  color = 'var(--color-text)' }:Props) => css`
  background-image: linear-gradient(${color}, ${color});
  background-position: ${reverse ? '100% 96%' : '0 96%'};
  background-repeat: no-repeat;
  background-size: ${`${reverse ? 100 : 0}% ${height}px`};
  position: relative;
  transition: background-size 300ms ease;

  &:hover,
  &.active {
    background-size: ${`${reverse ? 0 : 100}% ${height}px`};
  }

  &:not(:hover) {
    animation: ${reverse ? underlineReappear(height) : underlineDisappear(height)} 300ms ease;
  }
`

const underlineContract = (height: number) => keyframes`
  0% { background-size: 100% ${height}px; }
  100% { background-size: 0% ${height}px; }
`

export const createUnderlineMiddle = ({ height = 1, color = 'var(--color-text)' }: Props) => css`
  background-image: linear-gradient(${color}, ${color});
  background-position: center 96%;
  background-repeat: no-repeat;
  background-size: 0% ${height}px;
  position: relative;
  transition: background-size 300ms ease;

  &:hover,
  &.active {
    background-size:100% ${height}px;
  }

  &:not(:hover) {
    animation: ${underlineContract(height)} 300ms ease; /* Use different animations based on reverse */
  }
`
type AlphaStepNumber = 0 | .05 | .1 | .15 | .2 | .25 | .3 | .35 | .4 | .45 | .5 | .55 | .6 | .65 | .7 | .75 | .8 | .85 | .9 | .95 | 1
export function hex2Rgba(hex: string, alpha:AlphaStepNumber) {
  if (!/^#[0-9A-Fa-f]{6}$/i.test(hex)) console.warn('The hexadecimal color provider is not valid')
  const r = parseInt(hex.substring(1, 3), 16)
  const g = parseInt(hex.substring(3, 5), 16)
  const b = parseInt(hex.substring(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const rgbAlpha = (color:string, opacity:AlphaStepNumber) => css`${`color-mix(in srgb, ${`var(${color})`} ${opacity*100}%, transparent);`}`

export const hideScrollbar = () => css`
  -ms-overflow-style: none;  /* Internet Explorer 10+ */
  scrollbar-width: none;  /* Firefox */

  &::-webkit-scrollbar {
    background-color: rgba(0, 0, 0, 0);
    display: none;
  }
`

const defaultPlaceHolderStyles = (color) => css`
  transition: opacity 300ms ease-out;
  color: ${color ?? 'rgba(0, 0, 0, 0)'};
`

export const parsePlaceHolder = (styles, color = null) => (css`
  &::-webkit-input-placeholder { /* Chrome/Opera/Safari */
    ${defaultPlaceHolderStyles(color)}
    ${styles}
  }

  &::-moz-placeholder { /* Firefox 19+ */
    ${defaultPlaceHolderStyles(color)}
    ${styles}
  }
  &:-ms-input-placeholder { /* IE 10+ */
    ${defaultPlaceHolderStyles(color)}
    ${styles}
  }

  &:-moz-placeholder { /* Firefox 18- */
    ${defaultPlaceHolderStyles(color)}
    ${styles}
  }
`)


export const FigmaAnimations = {
  gentle: {
    damping: 15,
    mass: 1,
    stiffness: 100,
    type: 'spring'
  },
  quick: {
    damping: 20,
    mass: 1,
    stiffness: 300,
    type: 'spring'
  },
  bouncy: {
    damping: 15,
    mass: 1,
    stiffness: 600,
    type: 'spring'
  },
  slow: {
    damping: 20,
    mass: 1,
    stiffness: 80,
    type: 'spring'
  }
} as const

// Type for FigmaAnimations keys
type FigmaAnimationKey = keyof typeof FigmaAnimations
// Bezier curve mappings for each FigmaAnimation
const FIGMA_BEZIER_MAPPINGS = {
  gentle: [0.25, 0.1, 0.25, 1] as const,     // Smooth, gradual easing
  quick: [0.42, 0, 0.58, 1] as const,        // Fast start, quick settle
  bouncy: [0.68, -0.55, 0.265, 1.55] as const, // Overshoot and settle back
  slow: [0.25, 0.46, 0.45, 0.94] as const    // Very gradual, slow curve
} as const

/**
 * Converts a FigmaAnimations key to a valid CSS cubic-bezier curve
 * @param animationKey - The key from FigmaAnimations object ('gentle', 'quick', 'bouncy', 'slow')
 * @param duration - Optional duration in milliseconds (default: 300)
 * @param property - Optional CSS property to animate (default: 'all')
 * @returns CSS transition string with cubic-bezier curve
 *
 * @example
 * // Basic usage - returns cubic-bezier curve only
 * convertFigmaAnimationToBezier('gentle')
 * // Returns: 'cubic-bezier(0.25, 0.1, 0.25, 1)'
 *
 * @example
 * // With duration - returns full transition
 * convertFigmaAnimationToBezier('quick', 300)
 * // Returns: 'all 300ms cubic-bezier(0.42, 0, 0.58, 1)'
 *
 * @example
 * // With duration and property
 * convertFigmaAnimationToBezier('bouncy', 500, 'transform')
 * // Returns: 'transform 500ms cubic-bezier(0.68, -0.55, 0.265, 1.55)'
 */
export const convertFigmaAnimationToBezier = (
  animationKey: FigmaAnimationKey,
  duration?: number,
  property: string = 'all'
): string => {
  // Get the bezier curve for the animation key
  const bezierCurve = FIGMA_BEZIER_MAPPINGS[animationKey]

  if (!bezierCurve) {
    console.warn(`Invalid FigmaAnimation key: ${animationKey}. Falling back to 'gentle'.`)
    return convertFigmaAnimationToBezier('gentle', duration, property)
  }

  const [x1, y1, x2, y2] = bezierCurve
  const cubicBezier = `cubic-bezier(${x1}, ${y1}, ${x2}, ${y2})`

  // If no duration provided, return just the cubic-bezier curve
  if (duration === undefined) {
    return cubicBezier
  }

  // Return full CSS transition string
  return `${property} ${duration}ms ${cubicBezier}`
}


export const getVariants = (inView) => {
  const variants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: inView ? 1 : 0, y: 0, delay: .3 },
    exit: { opacity: 0, y: 10 },
    transition: { duration: 1, ease: 'easeInOut' }
  }
  return variants
}
